import { Status } from '@/Enums/Enums';
import type { LVColumnRow } from '@/Datastructures';
import type { LVColumn, ListView } from '@/Typings';
import type {
    ColumnFactory,
    LinkViewModel as LinkViewModelT,
    ImageViewModel as ImageViewModelT,
    ToolBarViewModel as ToolBarViewModelT,
    CheckBoxViewModel as CheckBoxViewModelT,
    AssociationGridViewModel as AssociationGridViewModelT,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    RADApplication,
    Utilities,
    Class,
    Factory,
    ComponentManager,
    GridViewModel as GridViewModelT,
    LabelViewModel as LabelViewModelT,
    GlobalConfiguration as Config,
    LocatorHelper,
} from 'radpresentation.htmlclient.defaultviewextensions';
import type ExtensionEditorViewModel from '@/ViewModels/Controls/ExtensionEditorViewModel';

class ListViewTabViewModel extends Class {
    private GridViewModel =
        ComponentManager.getViewModel<typeof GridViewModelT>('GridViewModel');
    private LinkViewModel =
        ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
    private LabelViewModel =
        ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private ImageViewModel =
        ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    private ToolBarViewModel =
        ComponentManager.getViewModel<typeof ToolBarViewModelT>(
            'ToolBarViewModel'
        );
    private CheckBoxViewModel =
        ComponentManager.getViewModel<typeof CheckBoxViewModelT>(
            'CheckBoxViewModel'
        );
    private AssociationGridViewModel = ComponentManager.getViewModel<
        typeof AssociationGridViewModelT
    >('AssociationGridViewModel');

    // member
    public ListViewGrid: AssociationGridViewModelT;
    private subscriptions: Array<KnockoutSubscription> = [];
    private currentLanguage: string;

    private moveUp: LinkViewModelT;
    private moveDown: LinkViewModelT;

    // injected
    public context: ExtensionEditorViewModel;
    public listViewColumns: Array<LVColumn>;
    public listView: ListView;

    public refreshGrid() {
        this.ListViewGrid.dataGrid.data.valueHasMutated();
    }

    public constructor(private locator: KnockoutObservable<string>) {
        super();

        this.currentLanguage = RADApplication.getInstance().currentLanguage();

        this.ListViewGrid = new this.AssociationGridViewModel({
            toolBar: this.createToolbar(),
            dataGrid: this.createDataGrid() as any, // kein DataGrid
            isDropable: false,
        });

        // sync moveUp/moveDown link enabledstate with the index of the currently selected row
        this.defineComputed(() => {
            let selected = this.ListViewGrid.dataGrid.selected()[0];
            if (!selected) {
                this.moveUp.isEnabled(false);
                this.moveDown.isEnabled(false);
            }

            ko.ignoreDependencies(() => {
                let allRows = this.ListViewGrid.dataGrid.data();
                let index = _.findIndex(allRows, e => e.PDO_ID == selected);
                let canMoveUp = index > 0;
                let canMoveDown = index >= 0 && index < allRows.length - 1;

                this.moveUp.isEnabled(canMoveUp);
                this.moveDown.isEnabled(canMoveDown);
            });
        });

        this.undisposeProperties.push('context');
    }

    public dispose(): void {
        if (!this.isDisposed) {
            this.disposeMySubscriptions();

            super.dispose();
        }
    }

    private disposeMySubscriptions() {
        if (!_.isEmpty(this.subscriptions)) {
            this.subscriptions.forEach(s => s.dispose());
            this.subscriptions = [];
        }
    }

    public populateGrid(dataUpdate: Array<LVColumnRow>) {
        this.disposeMySubscriptions();

        if (!_.isEmpty(dataUpdate)) {
            dataUpdate.forEach(row => {
                let sortabilitySubscription = row.SortabilityObs.subscribe(
                    newValue => {
                        row.Sortability = newValue;
                        row.lvColumnDto.Sortable = newValue;
                        Status.setChanged(row.lvColumnDto);
                    }
                );

                let visibilitySubscription = row.VisibilityObs.subscribe(
                    newValue => {
                        row.Visibility = newValue;
                        row.lvColumnDto.IsVisible = newValue;
                        Status.setChanged(row.lvColumnDto);
                    }
                );

                let filterabilitySubscription = row.FilterabilityObs.subscribe(
                    newValue => {
                        row.Filterability = newValue;
                        row.lvColumnDto.IsFilterable = newValue;
                        Status.setChanged(row.lvColumnDto);
                    }
                );

                this.subscriptions.push(sortabilitySubscription);
                this.subscriptions.push(visibilitySubscription);
                this.subscriptions.push(filterabilitySubscription);
            });
        }

        // Knockout erkennt keine Änderungen im Observable, da wir hier mit einer Objektstruktur arbeiten,
        // die während der Bearbeitung sich auch so schon im Observable befindet
        this.ListViewGrid.dataGrid.forceUpdateData(dataUpdate);
    }

    public removeLvColumn(listViewColumn: LVColumn) {
        let data = this.ListViewGrid.dataGrid.data;
        let test = dataEntry => dataEntry['PDO_ID'] === listViewColumn.PDO_ID;

        if (data().some(test)) {
            data(data().filter(dataEntry => !test(dataEntry)));
        }
    }

    private createToolbar() {
        this.moveUp = new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.MOVE_UP,
                alternativeText: new LabelViewModelT({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEUPHINT',
                }),
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEUPHINT',
            }),
            onClick: () => {
                this.moveUpProperty();
            },
            locator: LocatorHelper.buildLocator(this.locator, 'move_up'),
        });

        this.moveDown = new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.MOVE_DOWN,
                alternativeText: new LabelViewModelT({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEDOWNHINT',
                }),
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'RADEM.RADEXTENSIONMANAGER.MOVEDOWNHINT',
            }),
            onClick: () => {
                this.moveDownProperty();
            },
            locator: LocatorHelper.buildLocator(this.locator, 'move_down'),
        });

        return new this.ToolBarViewModel({
            primary: [this.moveUp, this.moveDown],
        });
    }

    private createDataGrid() {
        const visible = true;
        const hidden = false;

        return new this.GridViewModel({
            columnDefinition: [
                GridViewModelT.createColumnDefinition(
                    hidden,
                    'RADEM.RADEXTENSIONMANAGER.COLUMN_ID',
                    'PDO_ID'
                ),
                $.extend(
                    GridViewModelT.createColumnDefinition(
                        visible,
                        'RADEM.RADEXTENSIONMANAGER.COLUMN_NAME',
                        'DisplayName',
                        this.textColumn
                    ),
                    { width: '235px' }
                ),
                $.extend(
                    GridViewModelT.createColumnDefinition(
                        visible,
                        'RADEM.RADEXTENSIONMANAGER.COLUMN_TYPE',
                        'Typ',
                        this.dataTypeColumn
                    ),
                    { width: '184px' }
                ),
                $.extend(
                    GridViewModelT.createColumnDefinition(
                        visible,
                        'RADEM.RADEXTENSIONMANAGER.COLUMN_VISIBLE',
                        'Visibility',
                        this.visibleColumn,
                        'checkBoxColumn'
                    ),
                    {
                        width: '80px',
                        onAfterCellRender: (
                            cell: JQuery<Element>,
                            raw: any,
                            row: any,
                            rowIndex: number
                        ) => {
                            const checkBoxElement = cell.children().first();
                            const checkBoxViewModel = checkBoxElement.data(
                                'viewmodel'
                            ) as CheckBoxViewModelT;

                            checkBoxViewModel.parentLocator(
                                cell.data('locator')
                            );
                        },
                    }
                ),
                $.extend(
                    GridViewModelT.createColumnDefinition(
                        visible,
                        'RADEM.RADEXTENSIONMANAGER.COLUMN_SORTABLE',
                        'Sortability',
                        this.sortableColumn,
                        'checkBoxColumn'
                    ),
                    {
                        width: '80px',
                        onAfterCellRender: (
                            cell: JQuery<Element>,
                            raw: any,
                            row: any,
                            rowIndex: number
                        ) => {
                            const checkBoxElement = cell.children().first();
                            const checkBoxViewModel = checkBoxElement.data(
                                'viewmodel'
                            ) as CheckBoxViewModelT;

                            checkBoxViewModel.parentLocator(
                                cell.data('locator')
                            );
                        },
                    }
                ),
                $.extend(
                    GridViewModelT.createColumnDefinition(
                        visible,
                        'RADEM.RADEXTENSIONMANAGER.COLUMN_FILTERABLE',
                        'Filterability',
                        this.filterableColumn,
                        'checkBoxColumn'
                    ),
                    {
                        width: '80px',
                        onAfterCellRender: (
                            cell: JQuery<Element>,
                            raw: any,
                            row: any,
                            rowIndex: number
                        ) => {
                            const checkBoxElement = cell.children().first();
                            const checkBoxViewModel = checkBoxElement.data(
                                'viewmodel'
                            ) as CheckBoxViewModelT;

                            checkBoxViewModel.parentLocator(
                                cell.data('locator')
                            );
                        },
                    }
                ),
            ],
            data: [],
            identity: 'PDO_ID',
            locator: LocatorHelper.buildLocator(this.locator, 'lv_grid'),
        });
    }

    private dataTypeColumn = (raw: string, row: LVColumnRow) => {
        let escapedRaw = Utilities.encodeXmlChars(raw);

        let params = {
            raw: escapedRaw,
            isDynamic: row.lvColumnDto.IsDynamic,
            visibleObs: row.VisibilityObs,
            default: 'datatype.png',
            tooltip: row.lvColumnDto.IsAssoziation
                ? row.Typ
                : row.lvColumnDto.DotNetTypeName,
        };

        return this.createFactory('DataTypeColumnFactory', params).getValue();
    };

    private visibleColumn = (raw, row: LVColumnRow) => {
        let vm = new this.CheckBoxViewModel({
            isChecked: row['VisibilityObs'],
            isEnabled: row.lvColumnDto.IsEditable,
            locator: LocatorHelper.buildLocator(this.locator, 'visible'),
        });

        let params = {
            raw: raw,
            checkBoxViewModel: vm,
        };

        let $checkBox = this.createFactory(
            'CheckBoxControlColumn',
            params
        ).getValue() as JQuery<HTMLElement>;
        $checkBox.data('viewmodel', vm);

        return $checkBox;
    };

    private sortableColumn = (raw, row: LVColumnRow) => {
        let vm = new this.CheckBoxViewModel({
            isChecked: row['SortabilityObs'],
            isEnabled: this.defineComputed(
                () => row['VisibilityObs']() && row.lvColumnDto.IsEditable
            ),
            locator: LocatorHelper.buildLocator(this.locator, 'sortable'),
        });

        let params = {
            raw: raw,
            checkBoxViewModel: vm,
        };

        let $checkBox = this.createFactory(
            'CheckBoxControlColumn',
            params
        ).getValue() as JQuery<HTMLElement>;
        $checkBox.data('viewmodel', vm);

        return $checkBox;
    };

    private filterableColumn = (raw, row: LVColumnRow) => {
        let vm = new this.CheckBoxViewModel({
            isChecked: row['FilterabilityObs'],
            isEnabled: this.defineComputed(
                () => row['VisibilityObs']() && row.lvColumnDto.IsEditable
            ),
            locator: LocatorHelper.buildLocator(this.locator, 'filterable'),
        });

        let params = {
            raw: raw,
            checkBoxViewModel: vm,
        };

        let $checkBox = this.createFactory(
            'CheckBoxControlColumn',
            params
        ).getValue() as JQuery<HTMLElement>;
        $checkBox.data('viewmodel', vm);

        return $checkBox;
    };

    private textColumn = (raw: any, row: LVColumnRow) => {
        let resourceKeys = Object.keys(this.context.resourceItemsByErgName);
        let resourceKey = Utilities.findFirst(resourceKeys, key => {
            if (
                Utilities.isNullOrUndefinedOrBlank(row) ||
                Utilities.isNullOrUndefinedOrBlank(row.lvColumnDto) ||
                Utilities.isNullOrUndefinedOrBlank(row.lvColumnDto.ErgName)
            ) {
                return false;
            }
            return Utilities.equalsIgnoreCase(
                key,
                row.lvColumnDto.ErgName.toLowerCase()
            );
        });
        let resourceByLanguage = resourceKey
            ? this.context.resourceItemsByErgName[resourceKey]
            : null;

        let tooltip = '';
        if (resourceByLanguage && resourceByLanguage[this.currentLanguage])
            tooltip = resourceByLanguage[this.currentLanguage].Value;

        let params = {
            raw: raw,
            isDynamic: row.lvColumnDto.IsDynamic,
            visibleObs: row.VisibilityObs,
            tooltip: tooltip,
        };

        return this.createFactory(
            'ExtensionManagerTextColumn',
            params
        ).getValue();
    };

    private createFactory(identifier: string, params: any): ColumnFactory {
        let fallback = null;

        return Factory.createInstance(
            identifier,
            params,
            fallback
        ) as ColumnFactory;
    }

    private moveUpProperty() {
        let selected = this.ListViewGrid.dataGrid.selected()[0];
        if (!selected) {
            return;
        }

        // move in grid
        let allRows = this.ListViewGrid.dataGrid.data();
        let index = _.findIndex(allRows, e => e.PDO_ID == selected);
        if (index >= 1) {
            this.swap(allRows, index, index - 1);
            this.ListViewGrid.dataGrid.data.valueHasMutated();
            this.moveDown.isEnabled(true);
            this.moveUp.isEnabled(index > 1);
        }

        // move in data (json)
        let lvColumnIndex = _.findIndex(
            this.listViewColumns,
            e => e.PDO_ID == selected
        );
        if (lvColumnIndex >= 1) {
            this.swap(this.listViewColumns, lvColumnIndex, lvColumnIndex - 1);
            Status.setChanged(this.listView);
        }
    }

    private moveDownProperty() {
        let selected = this.ListViewGrid.dataGrid.selected()[0];

        if (!selected) {
            return;
        }

        // move in grid
        let allRows = this.ListViewGrid.dataGrid.data();
        let index = _.findIndex(allRows, e => e.PDO_ID == selected);
        if (index + 1 < allRows.length) {
            this.swap(allRows, index, index + 1);
            this.ListViewGrid.dataGrid.data.valueHasMutated();
            this.moveUp.isEnabled(true);
            this.moveDown.isEnabled(index + 2 < allRows.length);
        }

        // move in data (json)
        let lvColumnIndex = _.findIndex(
            this.listViewColumns,
            e => e.PDO_ID == selected
        );
        if (lvColumnIndex + 1 < allRows.length) {
            this.swap(this.listViewColumns, lvColumnIndex, lvColumnIndex + 1);
            Status.setChanged(this.listView);
        }
    }

    private swap(collection, firstIndex, secondIndex) {
        Utilities.swap(collection, firstIndex, secondIndex);
    }

    /* TODO: Nach TS-Portierung einkommentieren
    public dispose() {
        super.dispose();
        this.disposeSubscriptions();
    }
    */
}

export default ListViewTabViewModel;
